import WpToastManager from 'Wpds/components/toast/WpToastManager';
import * as api from 'Shared/api';
import { handleSuccess, handleError, showNotificationError } from 'Shared/helpers';
import { handleRequestError } from 'Shared/utils/handle-request-error';
import {
  acceptMember,
  bulkUpdateWorkspacesApi,
  applyTemplate,
  bulkApplyTemplate,
  saveTemplate,
  cancelTemplateWizard,
  getCaptureData,
  captureDataViewed,
  transfer,
  getRecentUpdates,
  fetchAgendaAiSlackChannels as fetchSlackChannels,
} from 'Shared/api/workspaces';
import moment from 'moment';

import WorkspaceRecentUpdatesSchema from 'Shared/utils/normalizeJSON/schemas/WorkspaceRecentUpdates.schema';
import normalizer from 'Shared/utils/normalizeJSON/normalizer';

export const clearWorkspace = () => ({
  type: 'CLEAR_WORKSPACE',
});

export const getWorkspaces =
  (input = '') =>
  (dispatch, getState) => {
    dispatch({ type: 'WORKSPACES_GET_REQUEST' });

    const state = getState();
    const {
      app: { id: appId },
    } = state;

    return api.getWorkspaces(input, { appId }).then(
      response => {
        const json = response.data;
        dispatch({
          type: 'WORKSPACES_GET_RESPONSE',
          data: json.data,
        });
        handleSuccess(response);
      },
      error => {
        dispatch(handleError(error));
      }
    );
  };

export const getWorkspace = workspaceId => (dispatch, getState) => {
  const state = getState();
  const {
    app: { id: appId },
    user: { jwt_token: token },
  } = state;

  dispatch({ type: 'WORKSPACE_GET_REQUEST' });

  return api.getWorkspace({ workspaceId, token, appId }).then(
    response => {
      dispatch({
        type: 'WORKSPACE_GET_RESPONSE',
        data: response.data.data,
      });
      return response;
    },
    error => {
      if (error.response && error.response.status === 404) {
        throw error;
      } else {
        dispatch(handleError(error));
      }
    }
  );
};

export const removeFromWorkspace = params => dispatch =>
  api.removeFromWorkspace(params).then(
    response => {
      dispatch({
        type: 'WORKSPACE_UPDATES_GET_RESPONSE',
        payload: response.data.data,
      });
      return response;
    },
    error => {
      dispatch(handleError(error));
      return error;
    }
  );

export const getTimelineGroups =
  ({ workspaceId, groupBy }) =>
  dispatch =>
    api.getTimelineGroupsForWorkspace({ workspaceId, groupBy }).then(
      response => {
        dispatch({
          type: 'WORKSPACE_TIMELINE_GROUPS_GET_RESPONSE',
          payload: response?.data?.timelineGroups || [],
        });
      },
      error => {
        dispatch(handleError(error));
      }
    );

export const updateWorkspace = (id, params) => dispatch =>
  api.updateWorkspace(id, params).then(
    response =>
      response.data.success &&
      dispatch({
        type: 'WORKSPACE_UPDATE_RESPONSE',
        payload: {
          id,
          params,
        },
      }) &&
      WpToastManager.success('Success'),
    error => {
      dispatch(handleError(error));
    }
  );

export const saveTemplateAction = data => dispatch => {
  return saveTemplate(data)
    .then(response => {
      dispatch({ type: 'WORKSPACE_UPDATE_RESPONSE', payload: response.data });
      return response;
    })
    .catch(error => {
      showNotificationError(error);
      return error.response.data.error;
    });
};

export const cancelTemplateWizardAction =
  ({ workspaceId }) =>
  dispatch => {
    return cancelTemplateWizard({ workspaceId }).then(response => {
      dispatch({
        type: 'WORKSPACE_UPDATE_RESPONSE',
        payload: { params: response.data.data },
      });
      return response;
    });
  };

export const applyTemplateAction =
  (params, options = {}) =>
  (dispatch, getState) => {
    const _options = {
      ...{ freshWorkspaceInRedux: true },
      ...options,
    };
    return applyTemplate({ ...params, clientSyncToken: getState().app.clientSyncToken }).then(
      response => {
        if (_options.freshWorkspaceInRedux) {
          dispatch({
            type: 'WORKSPACE_UPDATES_GET_RESPONSE',
            payload: response.data.data,
          });
        }
        return response;
      },
      error => {
        dispatch(handleError(error));
        return error;
      }
    );
  };

export const bulkApplyTemplateAction = data => dispatch => {
  return bulkApplyTemplate(data)
    .then(response => {
      dispatch({
        type: 'BULK_APPLY_WORKSPACE_TEMPLATE_SUCCESS',
        payload: response.data.data,
      });
      return response;
    })
    .catch(error => {
      dispatch(handleError(error));
      return error;
    });
};

export const resetWorkspaceNotifications = workspaceId => dispatch => {
  dispatch({
    type: 'RESET_WORKSPACE_NOTIFICATIONS',
    payload: {
      workspaceId,
      workspaceNotifications: {
        count: 0,
        modifiedItemsUuids: [],
        modifiedCommentsUuids: [],
        viewedWorkspace: true,
        viewedWorkspaceTime: moment().unix(),
      },
    },
  });
};

export const syncWorkspaceNotificationsAction = data => (dispatch, getState) => {
  const currentWorkspaceId = getState().workspace.id;
  dispatch({
    type: 'WORKSPACE_NOTIFICATIONS_SYNC',
    payload: {
      workspaceId: data.workspaceId,
      workspaceNotifications: {
        count: currentWorkspaceId === data.workspaceId ? 0 : data.workspaceNotifications.count,
        ...data.workspaceNotifications,
      },
    },
  });
  if (currentWorkspaceId === data.workspaceId) api.resetWorkspaceNotifications(currentWorkspaceId);
};

export const partialResetWorkspaceNotifications = workspaceId => (dispatch, getState) => {
  const prevWorkspaceNotifications = getState().workspace?.workspaceNotifications;

  if (prevWorkspaceNotifications?.count || prevWorkspaceNotifications?.viewedWorkspace === false)
    handleRequestError(api.resetWorkspaceNotifications(workspaceId)).then(() => {
      dispatch({
        type: 'RESET_WORKSPACE_NOTIFICATIONS',
        payload: {
          workspaceId,
          workspaceNotifications: {
            count: 0,
            modifiedItemsUuids: prevWorkspaceNotifications?.modifiedItemsUuids || [],
            modifiedCommentsUuids: prevWorkspaceNotifications?.modifiedCommentsUuids || [],
            viewedWorkspace: true,
            viewedWorkspaceTime: prevWorkspaceNotifications?.viewedWorkspaceTime,
          },
        },
      });
    });
};

export const deleteRecurrenceInWorkspace =
  ({ workspaceId }) =>
  dispatch =>
    api.deleteRecurrenceInWorkspace({ workspaceId }).then(response => {
      if (response?.status === 200) {
        dispatch({
          type: 'DELETE_WORKSPACE_RECURRENCE_RESPONSE',
          payload: {
            workspaceId,
            recurrence: undefined,
            manualNextEventDate: undefined,
          },
        });
      }
    });

export const setWorkspaceRecurrence =
  ({ workspaceId, recurrence }) =>
  dispatch =>
    api.setRecurrenceInWorkspace({ workspaceId, recurrence }).then(response => {
      if (response?.status === 200) {
        dispatch({
          type: 'SET_WORKSPACE_RECURRENCE_RESPONSE',
          payload: {
            workspaceId,
            recurrence: response.data,
            manualNextEventDate: undefined,
          },
        });
      }
    });

export const setCalendarMeeting =
  ({ calendarMeeting, workspaceId, page }) =>
  (dispatch, getState) => {
    const prevCalendarMeeting = getState().workspace.calendarMeeting;
    dispatch({
      type: 'SET_CALENDAR_MEETING_REQUEST',
      payload: {
        workspaceId,
        calendarMeeting,
      },
    });

    return api
      .setCalendarMeetingInWorkspace({ calendarMeetingId: calendarMeeting?.id, workspaceId, page })
      .then(response => {
        if (response?.status === 200) {
          dispatch({
            type: 'SET_CALENDAR_MEETING_RESPONSE',
            payload: {
              workspaceId,
              calendarMeeting: response.data.calendarMeeting,
            },
          });
          return response;
        }
      })
      .catch(error => {
        dispatch({
          type: 'SET_CALENDAR_MEETING_ERROR',
          payload: {
            workspaceId,
            calendarMeeting: prevCalendarMeeting,
          },
        });
        return error;
      });
  };

export const setRecurringMeeting =
  ({ recurringMeeting, workspaceId, page }) =>
  (dispatch, getState) => {
    const prevRecurringMeeting = getState().workspace.recurringMeeting;
    dispatch({
      type: 'SET_RECURRING_MEETING_REQUEST',
      payload: {
        workspaceId,
        recurringMeeting,
      },
    });

    return api
      .setRecurringMeetingInWorkspace({ recurringMeetingId: recurringMeeting?.id, workspaceId, page })
      .then(response => {
        if (response?.status === 200) {
          dispatch({
            type: 'SET_RECURRING_MEETING_RESPONSE',
            payload: {
              workspaceId,
              recurringMeeting: response.data.recurringMeeting,
            },
          });
        }
        return response;
      })
      .catch(error => {
        dispatch({
          type: 'SET_RECURRING_MEETING_ERROR',
          payload: {
            workspaceId,
            recurringMeeting: prevRecurringMeeting,
          },
        });
        return error;
      });
  };

export const archiveWorkspace = workspace => dispatch =>
  api.archiveWorkspace(workspace).then(
    response => {
      handleSuccess(response);
    },
    error => {
      dispatch(handleError(error));
    }
  );

export const transferWorkspace =
  ({ workspaceId, fromUserId, toUserId }) =>
  dispatch =>
    transfer({ workspaceId, fromUserId, toUserId }).then(
      response => {
        handleSuccess(response);
        return response;
      },
      error => {
        dispatch(handleError(error));
        return error;
      }
    );

export const bulkCreateWorkspaces =
  ({
    workspacesParams,
    showDefaultSuccessMessage = true,
    throwError = false,
    doNotTerminateOnErrors = false,
    inviteMethod = '',
    returnAll = false,
  }) =>
  async dispatch => {
    try {
      const response = await api.bulkCreateWorkspaces({ workspacesParams, doNotTerminateOnErrors, inviteMethod });
      if (response && response.status === 200) {
        const workspaces = response.data.workspaces;
        const members = response.data.members;
        const organization = response.data.organization;
        const consumerEmailsFromInvitees = response.data.consumer_emails_from_invitees;
        const workspaceSaveErrors = response.data.workspace_save_errors;

        dispatch({ type: 'ADD_WORKSPACES_RESPONSE', payload: { workspaces } });
        if (members) dispatch({ type: 'ADD_MEMBER_RESPONSE', payload: { members } });
        if (organization) dispatch({ type: 'UPDATE_ORGANIZATION_RESPONSE', payload: { organization } });

        if (showDefaultSuccessMessage && workspaces.length) {
          const successMessage = `You successfully added ${workspaces.map(({ name }) => `"${name}"`).join(', ')}!`;
          WpToastManager.success(successMessage);
        }

        if (consumerEmailsFromInvitees?.length || workspaceSaveErrors?.length) {
          let combinedErrorMessage = '';
          if (consumerEmailsFromInvitees?.length)
            combinedErrorMessage += `We did not invite these emails because they are not work emails: ${consumerEmailsFromInvitees.join(
              ', '
            )}`;
          if (workspaceSaveErrors?.length) {
            if (combinedErrorMessage.length) combinedErrorMessage += '\n';
            combinedErrorMessage += `There were issues creating some workspaces: ${workspaceSaveErrors.join(', ')}`;
          }

          WpToastManager.warning(combinedErrorMessage);
        }

        if (returnAll) return workspaces;
        return workspaces[0];
      }
      if (throwError) throw new Error('Something went wrong!');
      return WpToastManager.danger('Something went wrong!');
    } catch (e) {
      showNotificationError(e);
      if (throwError) throw e;
      return e;
    }
  };

export const bulkUpdateWorkspaces =
  ({ workspacesParams, throwError = false }) =>
  async dispatch => {
    try {
      const response = await bulkUpdateWorkspacesApi({ workspacesParams });
      if (response && response.status === 200) {
        const workspaces = response.data.workspaces;
        const members = response.data.members;
        const organization = response.data.organization;

        const linkedRecurringMeetings = workspacesParams
          .map(({ recurringMeetingId }) => recurringMeetingId)
          .filter(m => m);
        if (linkedRecurringMeetings.length)
          dispatch({
            type: 'LINK_RECURRING_MEETINGS_RESPONSE',
            payload: { recurringMeetingsIds: linkedRecurringMeetings },
          });

        dispatch({ type: 'UPDATE_WORKSPACES_RESPONSE', payload: { workspaces } });
        if (members) dispatch({ type: 'ADD_MEMBER_RESPONSE', payload: { members } });
        if (organization) dispatch({ type: 'UPDATE_ORGANIZATION_RESPONSE', payload: { organization } });

        return workspaces[0];
      }
      if (throwError) throw new Error('Something went wrong!');
      return WpToastManager.danger('Something went wrong!');
    } catch (e) {
      showNotificationError(e);
      if (throwError) throw e;
      return e;
    }
  };

export const acceptMemberAction =
  ({ memberSlug }) =>
  async dispatch => {
    try {
      const response = await acceptMember({ memberSlug });
      if (response && response.status === 200) {
        dispatch({ type: 'ADD_WORKSPACES_RESPONSE', payload: { workspaces: [response.data.workspace] } });
      }
      return response;
    } catch (e) {
      showNotificationError(e);
      return e;
    }
  };

export const fetchCaptureData =
  ({ workspaceId, params }) =>
  dispatch => {
    return getCaptureData({ workspaceId, params }).then(response => {
      dispatch({ type: 'FETCH_CAPTURE_DATA_SUCCESS', payload: response.data });
      return response.data;
    });
  };

export const viewCaptureData =
  ({ workspaceId, uuids }) =>
  dispatch => {
    return captureDataViewed({ workspaceId, uuids }).then(response => {
      dispatch({ type: 'VIEW_CAPTURE_DATA_SUCCESS', payload: response.data });
      return response.data;
    });
  };

export const fetchRecentUpdates =
  ({ workspaceId }) =>
  dispatch => {
    return getRecentUpdates({ workspaceId }).then(response => {
      dispatch({
        type: 'workspaces/FETCH_RECENT_UPDATES_SUCCESS',
        payload: normalizer(WorkspaceRecentUpdatesSchema, response.data),
      });
      return response.data;
    });
  };

export const duplicateItem =
  ({
    itemUuid,
    fromWorkspaceId,
    toWorkspaceIds,
    comment,
    assignToOtherUser,
    duplicateWithinWorkspaceUserIds,
    deleteDuplicatedItem = false,
  }) =>
  () => {
    return api.duplicateTodo({
      itemUuids: [itemUuid],
      duplicateFromWorkspaceId: fromWorkspaceId,
      duplicateToWorkspaceIds: toWorkspaceIds,
      isMoveItems: false,
      comment: comment,
      assignToOtherUser,
      duplicateWithinWorkspaceUserIds,
      deleteDuplicatedItem,
    });
  };

export const fetchAgendaAiSlackChannels = ({ workspaceId }) =>
  fetchSlackChannels({ workspaceId }).then(response => response);

export const starWorkspace = workspaceId => dispatch =>
  api.starWorkspace(workspaceId).then(
    response => {
      dispatch({
        type: 'WORKSPACE_STARRED_SUCCESS',
        payload: response.data,
      });
      handleSuccess(response);
    },
    error => {
      dispatch(handleError(error));
    }
  );

export const unstarWorkspace = workspaceId => dispatch =>
  api.unstarWorkspace(workspaceId).then(
    response => {
      dispatch({
        type: 'WORKSPACE_UNSTARRED_SUCCESS',
        payload: response.data,
      });
      handleSuccess(response);
    },
    error => {
      dispatch(handleError(error));
    }
  );
